import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, RefreshCw, Download, Code, Type, ChevronDown, ChevronUp, AlertTriangle, Info, Eye, MessageSquare, MessageSquarePlus, Pencil, Loader2, PenLine } from 'lucide-react';
import { confidencePercent, confidenceLevel } from '../utils/confidence';
import DocumentFeedback from './DocumentFeedback';
import DownloadMenu from './DownloadMenu';
import AnnotatedTranslation from './AnnotatedTranslation';
import DeepLComparison from './DeepLComparison';
import AnnotationLegend from './AnnotationLegend';
import { useAuth } from '../context/AuthContext';
import { canCompareWith } from '../constants/features';
import { DOWNLOAD_FORMATS } from '../constants/documents';
import { refusalReason } from '../services/downloadRefusal';
import './ChatInterface.css';

function ChatInterface({ messages, onSendMessage, onRegenerate, disabled, document, translating, onCompareClick, lowConfidenceCount = 0, onDownload, showOverlayButton = false, overlayAvailable = false, overlayPending = false, onEditOverlay }) {
  const { t } = useTranslation(['chat', 'common']);
  const { user } = useAuth();
  const deeplComparison = canCompareWith(user, 'deepl');
  const [input, setInput] = useState('');
  // Which file's onDownload() is in flight, so its button shows a spinner - a large file downloads for
  // seconds with no browser feedback otherwise.
  const [downloadingType, setDownloadingType] = useState(null);
  // Held with its document: this component stays mounted as the person goes to another chat.
  const [refusal, setRefusal] = useState(null);
  const [viewMode, setViewMode] = useState('html'); // 'html' | 'text'
  const [expandedMessages, setExpandedMessages] = useState({}); // { messageIndex: true/false }
  // Chat input is hidden by default - 95% of users just download and leave.
  // Users who need refinements click "Adjust translation" to reveal it.
  const [inputRevealed, setInputRevealed] = useState(false);
  const messagesEndRef = useRef(null);
  // Suppress auto-scroll on initial mount so opening a chat (whether
  // mid-translation or completed) never yanks the user down past the
  // header / document preview. Subsequent message additions still
  // scroll into view - but only when no translation/scan is in flight.
  const hasMountedRef = useRef(false);

  const COLLAPSED_HEIGHT = 440; // px - taller default preview; "show more" expands to full

  // The OCR model grades every scanned page; anything below "good" means it
  // may have misread the page. Translation confidence says nothing about that,
  // so warn separately instead of leaving a high score to imply the text is right.
  const scanQuality = document?.metadata?.scan_quality;
  // Text OCR never captured is missing outright - a more specific warning that replaces "may be misread".
  const scanUncapturedWords = document?.metadata?.scan_uncaptured_words;
  // Pages the model could not arrange keep their text but are set as plain lines.
  const scanPlainTextPages = document?.metadata?.scan_plain_text_pages || [];
  // Pages the automatic checks still found differing from the original: the scan's Word copy, and the
  // layout of its translation.
  const scanPagesToCheck = [...new Set([
    ...(document?.metadata?.scan_pages_to_check || []),
    ...(document?.metadata?.translation_pages_to_check || []),
  ])].sort((a, b) => a - b);
  // Boxes that shipped in the source language (model declined / dropped the tag).
  // A few is tolerated instead of failing the whole scan, but the user must know
  // which to review.
  const segFallbackCount = document?.metadata?.seg_fallback_count || 0;
  // A scan uploaded only to be read: its one deliverable is the editable copy, there is no translation to adjust.
  const recognitionOnly = document?.service === 'recognition';
  // Handwritten values of the scan's copy, each to be checked against its place on the scan in Compare.
  const handwrittenCount = document?.metadata?.scan_handwriting?.length || 0;

  useEffect(() => {
    // Keep the view at the TOP on the initial history load - only start
    // auto-scrolling once a genuinely new message arrives during the session.
    if (!hasMountedRef.current) {
      if (messages.length > 0) hasMountedRef.current = true;
      return;
    }
    if (translating) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, translating]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (input.trim() && !disabled) {
      onSendMessage(input.trim());
      setInput('');
    }
  };

  const formatTimestamp = (timestamp) => {
    const utcString = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
    return new Date(utcString).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const stripHtml = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const text = doc.body.textContent || '';
    return text.split('\n').map(line => line.trim()).filter(line => line).join('\n');
  };

  // Only the copy and the translation have a format; a scan's source files and its 1:1 copy are what they are.
  const runDownload = async (type, format) => {
    if (downloadingType || !onDownload) return;
    setDownloadingType(type);
    setRefusal(null);
    try {
      await onDownload(type, format);
    } catch (error) {
      const reason = await refusalReason(error);
      if (reason === null) {
        console.error('Download failed:', error);
        alert(t('download.failed'));
      } else {
        const unsupported = format === DOWNLOAD_FORMATS.idml && error.response.status === 422;
        setRefusal({ documentId: document.id, reason, unsupported });
      }
    } finally {
      setDownloadingType(null);
    }
  };

  // The document deliverables, shown once above the rating: the original and the translation, each in its formats.
  const renderDocActions = () => {
    if (!document) return null;
    const busy = downloadingType !== null;
    const scanned = !!document.metadata?.is_scanned;
    // When the scan's 1:1 overlay is ready it is the primary deliverable.
    const overlayIsHero = !!(scanned && showOverlayButton && overlayAvailable);
    // While the edit is being assembled the stored DOCX is still the pre-edit
    // one, so it is not offered as the edited translation.
    const docxRebuild = document.metadata?.docx_rebuild || null;
    const docxRebuilding = docxRebuild === 'running';
    const docxRebuildWarning = docxRebuild === 'failed'
      ? t('header.docxRebuildFailedTooltip')
      : docxRebuild === 'unsupported'
        ? t('header.docxRebuildUnsupportedTooltip')
        : null;
    const docxWarning = docxRebuildWarning ? (
      <p className="doc-actions-warning" role="status">
        <AlertTriangle size={14} />
        <span>{docxRebuildWarning}</span>
      </p>
    ) : null;
    const refusalWarning = refusal?.documentId === document.id ? (
      <p className="doc-actions-warning" role="alert" data-testid="download-refusal">
        <AlertTriangle size={14} />
        <span>
          {refusal.unsupported && t('download.indesignRefused')}
          <span className={refusal.unsupported ? 'doc-actions-warning-reason' : undefined}>{refusal.reason}</span>
        </span>
      </p>
    ) : null;

    const indesignItem = (type, testId) => ({
      type,
      format: DOWNLOAD_FORMATS.idml,
      label: t('header.formatIndesign'),
      hint: t('header.indesignHint'),
      title: t('header.indesignTooltip'),
      testId,
    });
    const originalItems = [
      {
        type: 'original',
        format: DOWNLOAD_FORMATS.docx,
        label: t('header.formatWord'),
        hint: t('header.formatWordHint'),
        title: t('header.originalWordTooltip'),
        testId: 'download-original-word',
      },
      indesignItem('original', 'download-original-idml'),
    ];
    if (document.minio_scan_source_key) {
      originalItems.push({
        type: 'scan_source',
        label: t('header.uploadedFile'),
        hint: t('header.uploadedFileHint'),
        title: t('header.uploadedFileTooltip'),
        testId: 'download-uploaded-file',
      });
    }
    if (overlayIsHero && document.minio_scan_source_key) {
      originalItems.push({
        type: 'before_after',
        label: t('header.beforeAfter'),
        hint: t('header.beforeAfterHint'),
        title: t('header.beforeAfterTooltip'),
        testId: 'download-before-after',
      });
    }
    const translationItems = [
      {
        type: 'translated',
        format: DOWNLOAD_FORMATS.docx,
        label: t('header.formatWord'),
        hint: t('header.formatWordHint'),
        title: t('header.translationTooltip'),
        testId: 'download-translated-word',
      },
      indesignItem('translated', 'download-translated-idml'),
    ];
    const downloadingOriginal = originalItems.some((item) => item.type === downloadingType);
    const downloadingTranslation = downloadingType === 'translated';
    let translationLabel = t('header.downloadTranslation');
    if (docxRebuilding) translationLabel = t('header.docxRebuilding');
    else if (downloadingTranslation) translationLabel = t('download.downloading');

    return (
      <div className="doc-actions">
        <DownloadMenu
          label={downloadingOriginal ? t('download.downloading') : t('header.downloadOriginal')}
          items={originalItems}
          onPick={runDownload}
          busy={downloadingOriginal}
          disabled={busy}
          primary={recognitionOnly}
          className="doc-actions-original"
          testId="download-original"
        />
        {!recognitionOnly && (
          <DownloadMenu
            label={translationLabel}
            items={translationItems}
            onPick={runDownload}
            busy={downloadingTranslation || docxRebuilding}
            disabled={busy || !document.minio_translated_key}
            primary={!overlayIsHero}
            title={docxRebuilding ? t('header.docxRebuildingTooltip') : undefined}
            className="doc-actions-translation"
            testId="download-translated"
          />
        )}
        {scanned && showOverlayButton && (
          <div className="action-split">
            <button
              className={`btn ${overlayIsHero ? 'btn-primary' : 'btn-secondary'} action-split-main`}
              onClick={() => runDownload('overlay')}
              disabled={!overlayAvailable || busy}
              title={
                overlayAvailable
                  ? t('header.overlayTooltip', 'Download a faithful 1:1 copy - the translation painted onto the original scan, keeping the exact layout')
                  : overlayPending
                    ? t('header.overlayPendingTooltip', 'The 1:1 copy is still being prepared - reopen this document in a moment to download it.')
                    : t('header.overlayUnavailableTooltip', "The 1:1 copy isn't available for this document (recognition was incomplete or the service was unavailable). The translated DOCX is still available.")
              }
            >
              {downloadingType === 'overlay' ? <Loader2 size={16} className="btn-spin" /> : <Download size={16} />}
              <span>{t('header.overlay', 'Download translation (original look)')}</span>
            </button>
            {overlayAvailable && (
              <button
                className="btn action-split-edit"
                onClick={onEditOverlay}
                title={t('header.editOverlayTooltip', 'Edit the translated text on the 1:1 copy, then re-render it')}
                aria-label={t('header.editOverlay', 'Edit 1:1 copy')}
              >
                <Pencil size={16} />
              </button>
            )}
          </div>
        )}
        {docxWarning}
        {refusalWarning}
      </div>
    );
  };

  return (
    <div className="chat-interface">
      <div className="messages-container">
        {messages.length === 0 && !document && !translating ? (
          <div className="empty-messages">
            <p>{t('messages.empty')}</p>
          </div>
        ) : messages.length === 0 && (document || translating) ? (
          <div className="empty-messages processing">
            <span className="processing-badge">
              <Loader2 size={16} className="btn-spin" />
              {t('messages.inProgress')}
            </span>
          </div>
        ) : (
          // Index of the LAST assistant message - the one that carries the
          // download buttons + affordances. Computed by role, NOT position:
          // sending a chat message optimistically appends a USER message, so
          // `messages.length - 1` would point past the assistant reply and every
          // download button would vanish until the next reply lands.
          (() => { const lastAssistantIndex = messages.map((m) => m.role).lastIndexOf('assistant'); return (
          messages.map((message, index) => {
            const isExpanded = expandedMessages[index] ?? false;
            const isAssistant = message.role === 'assistant';
            // Only the latest assistant message gets the DeepL "second opinion"
            // affordance - older translations would clutter the chat.
            const isLatestAssistant = isAssistant && index === lastAssistantIndex;
            // Pulse the DeepL button only when the message is fresh (just arrived).
            // We use the message timestamp so a page reload doesn't re-pulse for
            // translations done long ago.
            const messageAgeMs = message.timestamp
              ? Date.now() - new Date(message.timestamp).getTime()
              : Infinity;
            const isFreshlyTranslated = isLatestAssistant && messageAgeMs >= 0 && messageAgeMs < 8000;

            // Latest translation, collapsed: fill the remaining height so the
            // preview clamps to what fits and "Show more" stays on screen
            // (no messages-container scroll until the user expands).
            const collapsedFill = isLatestAssistant && !isExpanded;

            return (
              <div
                key={index}
                className={`message ${message.role}${collapsedFill ? ' message-fill' : ''}`}
              >
                {/* Controls for assistant messages */}
                {isAssistant && (
                  <div className="assistant-controls">
                    {isLatestAssistant && renderDocActions()}
                    <DocumentFeedback documentId={document?.id} />
                    {isLatestAssistant && document?.id && !recognitionOnly && (
                      <div className="comment-hint">
                        <MessageSquarePlus size={14} />
                        {t('comments.hint')}
                      </div>
                    )}
                    <div className="assistant-controls-row">
                      <div className="view-toggle">
                        <button
                          className={`toggle-btn ${viewMode === 'html' ? 'active' : ''}`}
                          onClick={() => setViewMode('html')}
                          title={t('view.showHtml')}
                        >
                          <Code size={14} />
                          {t('view.html')}
                        </button>
                        <button
                          className={`toggle-btn ${viewMode === 'text' ? 'active' : ''}`}
                          onClick={() => setViewMode('text')}
                          title={t('view.showText')}
                        >
                          <Type size={14} />
                          {t('view.text')}
                        </button>
                      </div>
                      {isLatestAssistant && message.metadata?.annotations?.length > 0 && (
                        <AnnotationLegend />
                      )}
                      {isLatestAssistant && document?.id && deeplComparison && !recognitionOnly && (
                        <DeepLComparison
                          documentId={document.id}
                          freshAttention={isFreshlyTranslated}
                        />
                      )}
                      {isLatestAssistant && !inputRevealed && !recognitionOnly && (
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm reveal-input-btn"
                          onClick={() => setInputRevealed(true)}
                          title={t('input.adjustTooltip', 'Open input to request adjustments or regenerate')}
                        >
                          <MessageSquare size={14} />
                          {t('input.adjustButton', 'Adjust translation')}
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {isLatestAssistant && scanUncapturedWords > 0 && (
                  <div className={`scan-quality-banner ${scanQuality === 'poor' ? 'poor' : 'fair'}`}>
                    <AlertTriangle size={14} />
                    <span>{t('scanUncaptured.warning', { count: scanUncapturedWords })}</span>
                  </div>
                )}
                {isLatestAssistant && !(scanUncapturedWords > 0) && scanQuality && scanQuality !== 'good' && (
                  <div className={`scan-quality-banner ${scanQuality}`}>
                    <AlertTriangle size={14} />
                    <span>{t(`scanQuality.${scanQuality}`)}</span>
                  </div>
                )}
                {isLatestAssistant && scanPlainTextPages.length > 0 && (
                  <div className="scan-quality-banner fair">
                    <AlertTriangle size={14} />
                    <span>{t('scanPlainPages.warning', { count: scanPlainTextPages.length, pages: scanPlainTextPages.join(', ') })}</span>
                  </div>
                )}
                {isLatestAssistant && document?.metadata?.uploaded_as_scan && (
                  <div className="scan-quality-banner info" data-testid="uploaded-as-scan">
                    <Info size={14} />
                    <span>{t('uploadedAsScan.note')}</span>
                  </div>
                )}
                {isLatestAssistant && scanPagesToCheck.length > 0 && (
                  <div className="scan-quality-banner fair" data-testid="scan-pages-to-check">
                    <AlertTriangle size={14} />
                    <span>{t('scanPagesToCheck.warning', { count: scanPagesToCheck.length, pages: scanPagesToCheck.join(', ') })}</span>
                  </div>
                )}
                {isLatestAssistant && segFallbackCount > 0 && (
                  <div className="scan-quality-banner fair">
                    <AlertTriangle size={14} />
                    <span>{t('scanFallback.warning', { count: segFallbackCount })}</span>
                  </div>
                )}
                {isLatestAssistant && scanQuality && (
                  <p className="scan-disclaimer">
                    <AlertTriangle size={13} className="scan-disclaimer-icon" />
                    <span>{t('common:scanDisclaimer')}</span>
                  </p>
                )}
                {isLatestAssistant && handwrittenCount > 0 && onCompareClick && (
                  <button
                    type="button"
                    className="confidence-summary-banner"
                    onClick={onCompareClick}
                    data-testid="scan-handwriting"
                  >
                    <PenLine size={14} />
                    <span className="confidence-summary-text">
                      {t('scanHandwriting.text', { count: handwrittenCount })}
                    </span>
                    <span className="confidence-summary-cta">
                      <Eye size={13} />
                      {t('confidenceBanner.cta', 'Open Compare')}
                    </span>
                  </button>
                )}
                {isLatestAssistant && lowConfidenceCount > 0 && onCompareClick && (
                  <button
                    type="button"
                    className="confidence-summary-banner"
                    onClick={onCompareClick}
                    title={t('confidenceBanner.tooltip', 'Open Compare view to inspect each segment')}
                  >
                    <AlertTriangle size={14} />
                    <span className="confidence-summary-text">
                      {t('confidenceBanner.text', {
                        count: lowConfidenceCount,
                        defaultValue: '{{count}} segments below 85% confidence',
                      })}
                    </span>
                    <span className="confidence-summary-cta">
                      <Eye size={13} />
                      {t('confidenceBanner.cta', 'Open Compare')}
                    </span>
                  </button>
                )}
                <div
                  className={`message-content ${isAssistant && !isExpanded ? 'collapsed' : ''}`}
                  style={isAssistant && !isExpanded && !collapsedFill ? { maxHeight: `${COLLAPSED_HEIGHT}px` } : {}}
                >
                  {isAssistant ? (
                    viewMode === 'html' ? (
                      <AnnotatedTranslation
                        html={message.content}
                        annotations={message.metadata?.annotations}
                        translatedLines={message.metadata?.translated_lines}
                        sourceLang={document?.language_from}
                        targetLang={document?.language_to}
                        documentId={document?.id}
                        enableComments={isLatestAssistant && !!document?.id && !recognitionOnly}
                      />
                    ) : (
                      <pre className="translation-text">{stripHtml(message.content)}</pre>
                    )
                  ) : (
                    <p>{message.content}</p>
                  )}
                </div>
                {isAssistant && (
                  <button
                    className="expand-collapse-btn"
                    onClick={() => setExpandedMessages(prev => ({ ...prev, [index]: !isExpanded }))}
                  >
                    {isExpanded ? (
                      <>
                        <ChevronUp size={16} />
                        {t('view.showLess')}
                      </>
                    ) : (
                      <>
                        <ChevronDown size={16} />
                        {t('view.showMore')}
                      </>
                    )}
                  </button>
                )}
                <div className="message-meta">
                  <span className="message-time">
                    {formatTimestamp(message.timestamp)}
                  </span>
                  {message.metadata?.confidence_score && (() => {
                    const pct = confidencePercent(message.metadata.confidence_score);
                    return (
                      <span className={`message-confidence level-${confidenceLevel(pct)}`}>
                        {t('confidence')}: {pct}%
                      </span>
                    );
                  })()}
                </div>
              </div>
            );
          })
          ); })()
        )}
        <div ref={messagesEndRef} />
      </div>

      {inputRevealed && (
        <form className="chat-input-form" onSubmit={handleSubmit}>
          <input
            type="text"
            className="input chat-input"
            placeholder={t('messages.placeholder')}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={disabled}
            autoFocus
          />
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onRegenerate}
            disabled={disabled || messages.length === 0}
            title={t('messages.regenerateTitle')}
          >
            <RefreshCw size={18} />
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={disabled || !input.trim()}
          >
            <Send size={18} />
          </button>
        </form>
      )}
    </div>
  );
}

export default ChatInterface;
